import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import {
  ActionIcon,
  Box,
  Button,
  Container,
  Grid,
  Group,
  LoadingOverlay,
  Stack,
  Text,
  TextInput,
  Tooltip,
} from '@mantine/core';
import { IconX } from '@tabler/icons-react';

import { useSearchProductsQuery } from '@api/products.api';

import useProductsFilters from '@hooks/useProductsFilters';

import ProductFilters from './productFilters/ProductFilters';
import ProductTable from './productTable/ProductTable';

export default function ProductsSearch() {
  const { t } = useTranslation();

  // ==========================================================================
  // General
  // ==========================================================================
  const [searchParams, setSearchParams] = useSearchParams();

  // ==========================================================================
  // State
  // ==========================================================================

  // General search query
  const [searchQuery, setSearchQuery] = useState(
    searchParams.get('searchQuery') || '',
  );

  const [confirmedSearchQuery, setConfirmedSearchQuery] = useState(
    searchParams.get('searchQuery') || '',
  );

  const marketCategory = useMemo(
    () => searchParams.get('market') || 'Cosmetico',
    [searchParams],
  );

  const paginationQueryParams = useMemo(
    () => ({
      page: +(searchParams.get('page') || 1),
      pageLength: +(searchParams.get('pageLength') || 10),
    }),
    [searchParams],
  );

  const {
    availableFilters,
    selectedFilters,
    changeSelectedFilters,
    loadMoreFilterValues,
    clearAllFilters,
    filtersFetchingState,
    setFilterSearchQuery,
  } = useProductsFilters({
    marketCategory,
    searchQuery: confirmedSearchQuery,
  });

  const showSearchResults = useMemo(
    () =>
      Object.values(selectedFilters).reduce(
        (acc, curr) => acc || curr.length > 0,
        false,
      ) || confirmedSearchQuery.trim() !== '',
    [selectedFilters, confirmedSearchQuery],
  );

  // ==========================================================================
  // Api
  // ==========================================================================
  const {
    data: products = {
      totalCount: 0,
      page: 1,
      totalPages: 1,
      products: [],
    },
    isError,
    isFetching: isFetchingProducts,
  } = useSearchProductsQuery(
    {
      marketCategory,
      ...selectedFilters,
      ...paginationQueryParams,
      searchQuery: confirmedSearchQuery,
    },
    {
      skip: !showSearchResults,
    },
  );

  // ==========================================================================
  // Handlers
  // ==========================================================================
  const searchProducts = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    setConfirmedSearchQuery(searchQuery);
    clearAllFilters(searchQuery);
  };

  const searchPlaceholderKey = useMemo(() => {
    switch (marketCategory) {
      case 'Nutraceutico':
        return 'search.searchByNutraceutical';
      case 'Farmaceutico':
        return 'search.searchByPharmaceutical';
      case 'Alimentare':
        return 'search.searchByFood';
      case 'Cosmetico':
        return 'search.searchByCosmetic';
      case 'Ricerca':
        return 'search.searchByLife';
    }
  }, [marketCategory]);

  // ==========================================================================
  // Render
  // ==========================================================================
  return (
    <Container p="xl" size="1450px">
      <Grid gutter="xl">
        <Grid.Col span={12}>
          <Group align="end">
            <form onSubmit={searchProducts} style={{ width: '100%' }}>
              <Group align="end">
                <TextInput
                  flex={1}
                  size="lg"
                  rightSection={
                    searchQuery.trim() !== '' ? (
                      <Tooltip label={t('search.clearSearch')}>
                        <ActionIcon
                          variant="subtle"
                          size="lg"
                          onClick={() => {
                            setSearchQuery('');
                            setConfirmedSearchQuery('');
                            setSearchParams((prev) => {
                              prev.delete('searchQuery');
                              return prev;
                            });
                          }}
                          disabled={isFetchingProducts}
                        >
                          <IconX />
                        </ActionIcon>
                      </Tooltip>
                    ) : undefined
                  }
                  placeholder={t(searchPlaceholderKey)}
                  onChange={(e) => setSearchQuery(e.currentTarget.value)}
                  value={searchQuery}
                />
                <Button type="submit" size="lg" loading={isFetchingProducts}>
                  {t('search.search')}
                </Button>
              </Group>
            </form>
          </Group>
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 4 }}>
          <ProductFilters
            availableFilters={availableFilters}
            selectedFilters={selectedFilters}
            changeSelectedFilters={changeSelectedFilters}
            loadMoreFilterValues={loadMoreFilterValues}
            clearAllFilters={() => clearAllFilters()}
            filtersFetchingState={filtersFetchingState}
            setFilterSearchQuery={setFilterSearchQuery}
            marketCategory={marketCategory}
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 8 }}>
          <Stack>
            {isError ? (
              <Text size="md" c="red" mt="xl" fs="italic" ta="center">
                {t('search.noProductsFound')}
              </Text>
            ) : (
              <Box pos="relative">
                <LoadingOverlay
                  visible={isFetchingProducts}
                  zIndex={1000}
                  overlayProps={{ radius: 'sm', blur: 2 }}
                />
                {!showSearchResults ? (
                  <Text size="lg" mb="md">
                    {t('search.selectFilterOrSearch')}
                  </Text>
                ) : confirmedSearchQuery ? (
                  <Text size="lg" mb="md">
                    {t('search.searchFor')}{' '}
                    <Text span c="brand" fw="bold">
                      {confirmedSearchQuery}
                    </Text>{' '}
                    {t('search.searchFound')}{' '}
                    <Text span c="brand" fw="bold">
                      {products?.totalCount}
                    </Text>{' '}
                    {products?.totalCount === 1
                      ? t('search.result')
                      : t('search.resultsPlural')}
                  </Text>
                ) : (
                  <Text size="lg" mb="md">
                    {t('search.searchFound')}{' '}
                    <Text span c="brand" fw="bold">
                      {products?.totalCount}
                    </Text>{' '}
                    {products?.totalCount === 1
                      ? t('search.result')
                      : t('search.resultsPlural')}
                  </Text>
                )}
                {showSearchResults && products && products.totalCount > 0 && (
                  <ProductTable
                    products={products!}
                    page={paginationQueryParams.page}
                    pageLength={paginationQueryParams.pageLength}
                    showConfColumn={marketCategory === 'Ricerca'}
                    marketCategory={marketCategory}
                  />
                )}
              </Box>
            )}
          </Stack>
        </Grid.Col>
      </Grid>
    </Container>
  );
}
